import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { api } from '../../utils/api'
import { useCurrency, type Currency } from '../../contexts/CurrencyContext'
import { normalizePrintingLabel } from '../../utils/variantLabeling'
import {
  CHART_COLORS,
  generateYTicks,
  buildLinePath,
  computeDomain,
  createScale,
  formatAxisDate,
  type ChartColors,
} from './chartUtils'
import styles from './PriceHistoryChart.module.css'

type PriceHistoryEntry = {
  id: string
  source: string
  priceCents: number
  currency: string
  variantData: Record<string, any>
  recordedAt: string
}

type DailyPoint = {
  dayKey: string // YYYY-MM-DD (UTC)
  timestamp: number // ms
  displayCents: number
  /** true when no sync ran that day and the previous day's price was carried forward */
  carried: boolean
}

type RangeKey = '7D' | '1M' | '3M' | '6M' | '1Y' | 'ALL'

const RANGE_OPTIONS: Array<{ key: RangeKey; label: string; days: number | null }> = [
  { key: '7D', label: '7D', days: 7 },
  { key: '1M', label: '1M', days: 30 },
  { key: '3M', label: '3M', days: 90 },
  { key: '6M', label: '6M', days: 180 },
  { key: '1Y', label: '1Y', days: 365 },
  { key: 'ALL', label: 'All', days: null },
]

const DAY_MS = 24 * 60 * 60 * 1000

const rangeKeyForDays = (days: number): RangeKey => {
  if (days <= 7) return '7D'
  if (days <= 30) return '1M'
  if (days <= 90) return '3M'
  if (days <= 180) return '6M'
  return '1Y'
}

const toUtcDayKey = (timestampMs: number): string => {
  const d = new Date(timestampMs)
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const utcDayKeyToNoonTimestamp = (dayKey: string): number => {
  const [y, m, d] = dayKey.split('-').map((part) => parseInt(part, 10))
  return Date.UTC(y, (m || 1) - 1, d || 1, 12, 0, 0, 0)
}

const normalizeCurrency = (currency: string | undefined | null, fallback: Currency): Currency => {
  const upper = (currency || '').toUpperCase()
  switch (upper) {
    case 'AUD':
    case 'USD':
    case 'GBP':
    case 'EUR':
    case 'JPY':
    case 'CNY':
      return upper as Currency
    default:
      return fallback
  }
}

const formatTooltipDate = (timestamp: number): string =>
  new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })

const freshnessLabel = (lastRealTimestamp: number | null): { text: string; stale: boolean } | null => {
  if (lastRealTimestamp === null) return null
  const ageDays = Math.floor((Date.now() - lastRealTimestamp) / DAY_MS)
  if (ageDays <= 0) return { text: 'Updated today', stale: false }
  if (ageDays === 1) return { text: 'Updated yesterday', stale: false }
  return { text: `Last market update ${ageDays} days ago`, stale: ageDays > 3 }
}

export const PriceHistoryChart = ({
  cardId,
  days = 180,
  source,
  title = 'Market Price History',
  printing,
  condition,
  variantId,
  chartHeight = 260,
  colors: colorOverrides,
}: {
  cardId: string
  /** Initial visible range in days (data for up to a year is always loaded) */
  days?: number
  /**
   * Force a pricing source. Leave unset: the API picks the source that owns the card's game
   * (Scryfall for Magic, JustTCG for the rest). Pinning 'justtcg' here is what left every
   * Magic chart blank — those rows stopped being written when Magic moved to Scryfall.
   */
  source?: string
  title?: string
  printing?: string | null
  condition?: string | null
  variantId?: string | null
  /** Chart area height in pixels */
  chartHeight?: number
  /** Override individual chart colors */
  colors?: Partial<ChartColors>
}) => {
  const c = { ...CHART_COLORS, ...colorOverrides }
  const chartId = useId().replace(/:/g, '')
  const { currency: displayCurrency, baseCurrency, tryConvertPrice, formatPrice } = useCurrency()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [history, setHistory] = useState<PriceHistoryEntry[]>([])
  const [range, setRange] = useState<RangeKey>(() => rangeKeyForDays(days))
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)

  const svgRef = useRef<SVGSVGElement | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const [tooltip, setTooltip] = useState<null | { anchorX: number; anchorY: number; left: number; top: number; title: string; body: string }>(null)

  useEffect(() => {
    setRange(rangeKeyForDays(days))
  }, [days])

  useLayoutEffect(() => {
    if (!tooltip) return
    const tip = tooltipRef.current
    if (!tip) return

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const tipWidth = tip.offsetWidth
    const tipHeight = tip.offsetHeight

    const padding = 8
    const offset = 14

    let desiredLeft = tooltip.anchorX + offset
    let desiredTop = tooltip.anchorY - tipHeight - offset
    if (desiredTop < padding) {
      desiredTop = tooltip.anchorY + offset
    }

    const maxLeft = Math.max(padding, viewportWidth - tipWidth - padding)
    const maxTop = Math.max(padding, viewportHeight - tipHeight - padding)
    const clampedLeft = Math.min(Math.max(padding, desiredLeft), maxLeft)
    const clampedTop = Math.min(Math.max(padding, desiredTop), maxTop)

    if (clampedLeft !== tooltip.left || clampedTop !== tooltip.top) {
      setTooltip((prev) => (prev ? { ...prev, left: clampedLeft, top: clampedTop } : prev))
    }
  }, [tooltip])

  useEffect(() => {
    let cancelled = false

    const fetchHistory = async () => {
      setLoading(true)
      setError(null)
      try {
        // Always load a full year so range switching is instant and client-side.
        const response = await api.get<any>(`/cards/${cardId}/price-history`, {
          params: {
            days: Math.max(days, 365),
            limit: 1000,
            ...(source ? { source } : {}),
            ...(printing ? { printing } : {}),
            ...(condition ? { condition } : {}),
            ...(variantId ? { variantId } : {})
          }
        })
        const payload = response.data?.data ?? response.data
        const items = Array.isArray(payload?.history) ? (payload.history as PriceHistoryEntry[]) : []
        if (!cancelled) {
          setHistory(items)
        }
      } catch (e: any) {
        const serverMessage = e?.response?.data?.message || e?.response?.data?.error
        if (!cancelled) {
          setError(serverMessage || 'Failed to load price history')
          setHistory([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void fetchHistory()
    return () => {
      cancelled = true
    }
  }, [cardId, days, source, printing, condition, variantId])

  const normalizeVariantToken = (value?: string | null): string => String(value || '').trim().toLowerCase()
  const normalizePrintingToken = (value?: string | null): string =>
    normalizeVariantToken(normalizePrintingLabel(value))
  const normalizeConditionToken = (value?: string | null): string => {
    const token = normalizeVariantToken(value)
    const aliases: Record<string, string> = {
      nm: 'near mint',
      lp: 'lightly played',
      mp: 'moderately played',
      hp: 'heavily played',
      dmg: 'damaged',
      dm: 'damaged',
    }
    return aliases[token] ?? token
  }

  const canonicalHistory = useMemo(() => {
    if (!history.length) return []

    const targetPrinting = normalizePrintingToken(printing)
    const targetCondition = normalizeConditionToken(condition)
    const targetVariantId = normalizeVariantToken(variantId)

    const hasVariantFilter = Boolean(targetPrinting || targetCondition || targetVariantId)

    const strictFiltered = hasVariantFilter
      ? history.filter((h) => {
          const variant = (h.variantData || {}) as Record<string, any>
          const variantPrinting = normalizePrintingToken(variant.printing)
          const variantCondition = normalizeConditionToken(variant.condition)
          const variantKey = normalizeVariantToken(variant.variantId || variant.id)

          if (targetVariantId && variantKey && variantKey !== targetVariantId) return false
          if (targetPrinting && variantPrinting !== targetPrinting) return false
          if (targetCondition && variantCondition !== targetCondition) return false
          return true
        })
      : history

    let filtered = strictFiltered
    if (hasVariantFilter && filtered.length === 0) {
      // Fallback 1: ignore variantId, match only printing/condition.
      filtered = history.filter((h) => {
        const variant = (h.variantData || {}) as Record<string, any>
        const variantPrinting = normalizePrintingToken(variant.printing)
        const variantCondition = normalizeConditionToken(variant.condition)

        if (targetPrinting && variantPrinting !== targetPrinting) return false
        if (targetCondition && variantCondition !== targetCondition) return false
        return true
      })
    }

    if (!filtered.length) return []

    if (hasVariantFilter) return filtered

    const canonical = filtered.filter((h) => Boolean(h.variantData?.canonical))
    return canonical.length ? canonical : filtered
  }, [history, printing, condition, variantId])

  // One point per UTC day (latest record wins), carried forward through gap days up
  // to today so the line always reaches the present instead of dying at the last sync.
  const continuousPoints = useMemo<DailyPoint[]>(() => {
    if (!canonicalHistory.length) return []

    const byDay = new Map<string, PriceHistoryEntry>()
    for (const entry of canonicalHistory) {
      const dayKey = toUtcDayKey(new Date(entry.recordedAt).getTime())
      const existing = byDay.get(dayKey)
      if (!existing || new Date(entry.recordedAt).getTime() > new Date(existing.recordedAt).getTime()) {
        byDay.set(dayKey, entry)
      }
    }

    const realDays = [...byDay.keys()].sort()
    if (!realDays.length) return []

    const points: DailyPoint[] = []
    const todayKey = toUtcDayKey(Date.now())
    let cursor = utcDayKeyToNoonTimestamp(realDays[0])
    const end = utcDayKeyToNoonTimestamp(todayKey)
    let lastDisplayCents = 0

    while (cursor <= end) {
      const dayKey = toUtcDayKey(cursor)
      const entry = byDay.get(dayKey)
      const fromCurrency = entry ? normalizeCurrency(entry.currency, baseCurrency) : null
      // A point with no usable rate is carried rather than plotted at a guessed conversion.
      const converted = entry && fromCurrency ? tryConvertPrice(entry.priceCents, fromCurrency, displayCurrency) : null
      if (entry && converted !== null) {
        lastDisplayCents = Math.round(converted)
        points.push({ dayKey, timestamp: cursor, displayCents: lastDisplayCents, carried: false })
      } else {
        points.push({ dayKey, timestamp: cursor, displayCents: lastDisplayCents, carried: true })
      }
      cursor += DAY_MS
    }

    return points
  }, [baseCurrency, canonicalHistory, tryConvertPrice, displayCurrency])

  // Market history is recorded in its source currency (USD from the price feeds) and every point
  // is converted here at TODAY's rate — fine for the shape of a market, but not what the card was
  // worth in this currency on the day. Say so whenever a conversion happened.
  const convertedFrom = useMemo(() => {
    const sources = new Set(canonicalHistory.map((entry) => normalizeCurrency(entry.currency, baseCurrency)))
    sources.delete(displayCurrency)
    return sources.size > 0 ? [...sources].join('/') : null
  }, [baseCurrency, canonicalHistory, displayCurrency])

  const visiblePoints = useMemo<DailyPoint[]>(() => {
    const option = RANGE_OPTIONS.find((o) => o.key === range)
    if (!option || option.days === null) return continuousPoints
    const cutoff = Date.now() - option.days * DAY_MS
    const sliced = continuousPoints.filter((p) => p.timestamp >= cutoff)
    return sliced.length ? sliced : continuousPoints.slice(-1)
  }, [continuousPoints, range])

  const summary = useMemo(() => {
    if (!visiblePoints.length) return null
    const first = visiblePoints[0]
    const last = visiblePoints[visiblePoints.length - 1]
    const delta = last.displayCents - first.displayCents
    const deltaPct = first.displayCents > 0 ? (delta / first.displayCents) * 100 : null
    const realPoints = continuousPoints.filter((p) => !p.carried)
    const lastRealTimestamp = realPoints.length ? realPoints[realPoints.length - 1].timestamp : null
    return {
      latest: last.displayCents,
      delta,
      deltaPct,
      min: Math.min(...visiblePoints.map((p) => p.displayCents)),
      max: Math.max(...visiblePoints.map((p) => p.displayCents)),
      freshness: freshnessLabel(lastRealTimestamp),
    }
  }, [continuousPoints, visiblePoints])

  const trend: 'up' | 'down' | 'flat' = !summary || summary.delta === 0 ? 'flat' : summary.delta > 0 ? 'up' : 'down'
  const trendColor =
    trend === 'up'
      ? 'var(--color-success, #16A34A)'
      : trend === 'down'
        ? 'var(--color-danger, #DC2626)'
        : 'var(--text-muted, #6B7280)'

  // Fixed SVG coordinate space; rendered responsively via viewBox.
  const width = 720
  const height = 260
  const padX = 56
  const padTop = 16
  const padBottom = 30

  const geometry = useMemo(() => {
    if (!visiblePoints.length) return null

    const prices = visiblePoints.map((p) => p.displayCents)
    const { min, max } = computeDomain(prices, { clampMin: 0 })

    let tMin = visiblePoints[0].timestamp
    let tMax = visiblePoints[visiblePoints.length - 1].timestamp
    if (tMin === tMax) { tMin -= DAY_MS / 2; tMax += DAY_MS / 2 }

    const scaleX = createScale(tMin, tMax, padX, width - padX)
    const scaleY = (p: number) => {
      const ratio = (p - min) / (max - min)
      return height - padBottom - ratio * (height - padTop - padBottom)
    }

    const points = visiblePoints.map((p) => ({
      ...p,
      x: scaleX(p.timestamp),
      y: scaleY(p.displayCents),
    }))

    const yTicks = generateYTicks(min, max)

    const tickCount = Math.min(6, points.length)
    const tickIndexes = tickCount <= 1
      ? [0]
      : [...new Set(Array.from({ length: tickCount }, (_, i) => Math.round((i * (points.length - 1)) / (tickCount - 1))))]

    const spanDays = (tMax - tMin) / DAY_MS

    return { points, yTicks, tickIndexes, scaleY, spanDays }
  }, [visiblePoints])

  const showTooltipForIndex = (index: number, anchorX: number, anchorY: number) => {
    if (!geometry) return
    const point = geometry.points[index]
    if (!point) return

    const prev = index > 0 ? geometry.points[index - 1] : null
    const dayDelta = prev ? point.displayCents - prev.displayCents : null
    const lines: string[] = [formatPrice(point.displayCents, displayCurrency)]
    if (dayDelta !== null && dayDelta !== 0) {
      lines.push(`${dayDelta > 0 ? '▲' : '▼'} ${formatPrice(Math.abs(dayDelta), displayCurrency)} vs prior day`)
    }
    if (point.carried) {
      lines.push('No sync this day — last known price')
    }

    setTooltip({
      anchorX,
      anchorY,
      left: anchorX,
      top: anchorY,
      title: formatTooltipDate(point.timestamp),
      body: lines.join('\n'),
    })
  }

  const handlePointerMove = (event: React.PointerEvent<SVGRectElement>) => {
    const svg = svgRef.current
    if (!svg || !geometry || !geometry.points.length) return

    // Map client coords back into viewBox space (preserveAspectRatio="xMidYMid meet").
    const rect = svg.getBoundingClientRect()
    const scale = Math.min(rect.width / width, rect.height / height)
    if (scale <= 0) return
    const offsetX = (rect.width - width * scale) / 2
    const svgX = (event.clientX - rect.left - offsetX) / scale

    let nearest = 0
    let nearestDist = Infinity
    for (let i = 0; i < geometry.points.length; i++) {
      const dist = Math.abs(geometry.points[i].x - svgX)
      if (dist < nearestDist) {
        nearestDist = dist
        nearest = i
      }
    }

    setHoverIndex(nearest)
    showTooltipForIndex(nearest, event.clientX, event.clientY)
  }

  const clearHover = () => {
    setHoverIndex(null)
    setTooltip(null)
  }

  const chart = (() => {
    if (loading) {
      return <div className={styles.placeholder}>Loading price history…</div>
    }

    if (error) {
      return <div className={styles.placeholder}>{error}</div>
    }

    if (!geometry || !geometry.points.length) {
      const hasFilter = Boolean(printing || condition || variantId)
      return (
        <div className={styles.placeholder}>
          {hasFilter
            ? 'No price history for this variant yet. History may become available after the next price sync.'
            : 'No price history yet'}
        </div>
      )
    }

    const { points, yTicks, tickIndexes, spanDays } = geometry
    const path = buildLinePath(points)
    const lastPoint = points[points.length - 1]
    const realPoints = points.filter((p) => !p.carried)
    const showDots = realPoints.length > 0 && realPoints.length <= 45
    const hoverPoint = hoverIndex !== null ? points[hoverIndex] : null

    return (
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={chartHeight}
        role="img"
        aria-label="Price history chart"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id={`phFill-${chartId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={trendColor} stopOpacity="0.22" />
            <stop offset="100%" stopColor={trendColor} stopOpacity="0" />
          </linearGradient>
        </defs>

        <rect x="0" y="0" width={width} height={height} fill={c.background} />

        {/* Grid + Y axis labels */}
        {yTicks.map((value) => {
          const y = geometry.scaleY(value)
          return (
            <g key={`y-${value}`}>
              <line x1={padX} x2={width - padX} y1={y} y2={y} stroke={c.gridLine} strokeWidth="1" />
              <text
                x={padX - 8}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                className={styles.axisLabel}
              >
                {formatPrice(Math.round(value), displayCurrency)}
              </text>
            </g>
          )
        })}

        {/* X axis labels */}
        {tickIndexes.map((idx) => {
          const p = points[idx]
          return (
            <g key={`x-${p.dayKey}`}>
              <line x1={p.x} x2={p.x} y1={height - padBottom} y2={height - padBottom + 4} stroke={c.axisTick} strokeWidth="1" />
              <text
                x={p.x}
                y={height - 8}
                textAnchor="middle"
                dominantBaseline="alphabetic"
                className={styles.axisLabel}
              >
                {formatAxisDate(p.timestamp, spanDays > 200 ? 'monthly' : 'daily')}
              </text>
            </g>
          )
        })}

        <line x1={padX} x2={width - padX} y1={height - padBottom} y2={height - padBottom} stroke={c.axisLine} strokeWidth="1" />

        {/* Area fill + price line */}
        <path
          d={`${path} L ${lastPoint.x} ${height - padBottom} L ${points[0].x} ${height - padBottom} Z`}
          fill={`url(#phFill-${chartId})`}
        />
        <path d={path} fill="none" stroke={trendColor} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />

        {/* Dots only on real sync days, and only when sparse enough to read */}
        {showDots && realPoints.map((p) => (
          <circle key={p.dayKey} cx={p.x} cy={p.y} r={3} fill={trendColor} stroke={c.dotStroke} strokeWidth="1.5" />
        ))}

        {/* Latest price marker */}
        <circle cx={lastPoint.x} cy={lastPoint.y} r={4.5} fill={trendColor} stroke={c.dotStroke} strokeWidth="2" />

        {/* Hover crosshair */}
        {hoverPoint && (
          <g pointerEvents="none">
            <line
              x1={hoverPoint.x}
              x2={hoverPoint.x}
              y1={padTop}
              y2={height - padBottom}
              stroke={c.axisTick}
              strokeWidth="1"
              strokeDasharray="3 3"
            />
            <circle cx={hoverPoint.x} cy={hoverPoint.y} r={5} fill={trendColor} stroke={c.dotStroke} strokeWidth="2" />
          </g>
        )}

        {/* Full-area pointer target (much easier to hit than per-dot hover) */}
        <rect
          x={padX}
          y={padTop}
          width={width - padX * 2}
          height={height - padTop - padBottom}
          fill="transparent"
          onPointerMove={handlePointerMove}
          onPointerDown={handlePointerMove}
          onPointerLeave={clearHover}
        />
      </svg>
    )
  })()

  return (
    <div className={styles.wrap} style={{ ['--chart-height' as any]: `${chartHeight}px` }}>
      <div className={styles.headerRow}>
        <div className={styles.titleBlock}>
          <div className={styles.title}>{title}</div>
          {convertedFrom && (
            <div className={styles.freshness}>{convertedFrom} prices converted at today&apos;s rate</div>
          )}
          {summary?.freshness && (
            <div className={`${styles.freshness} ${summary.freshness.stale ? styles.freshnessStale : ''}`}>
              {summary.freshness.text}
            </div>
          )}
        </div>

        <div className={styles.rangeGroup} role="group" aria-label="Chart range">
          {RANGE_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`${styles.rangeBtn} ${range === option.key ? styles.rangeBtnActive : ''}`}
              onClick={() => {
                setRange(option.key)
                clearHover()
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {summary && (
        <div className={styles.statsRow}>
          <span className={styles.latestPrice}>{formatPrice(summary.latest, displayCurrency)}</span>
          <span
            className={`${styles.deltaBadge} ${trend === 'up' ? styles.deltaUp : trend === 'down' ? styles.deltaDown : styles.deltaFlat}`}
            title={`Change over ${range === 'ALL' ? 'all available history' : range}`}
          >
            <span className={styles.deltaPeriod}>{range === 'ALL' ? 'All' : range}</span>
            {trend === 'up' ? '▲' : trend === 'down' ? '▼' : '•'}{' '}
            {formatPrice(Math.abs(summary.delta), displayCurrency)}
            {summary.deltaPct !== null && summary.delta !== 0 && (
              <> ({summary.deltaPct > 0 ? '+' : ''}{summary.deltaPct.toFixed(1)}%)</>
            )}
          </span>
          <span className={styles.statPill}>High {formatPrice(summary.max, displayCurrency)}</span>
          <span className={styles.statPill}>Low {formatPrice(summary.min, displayCurrency)}</span>
        </div>
      )}

      <div className={styles.chartArea}>
        <div className={styles.chartInner} onMouseLeave={clearHover}>
          {chart}
        </div>
      </div>
      {tooltip &&
        createPortal(
          <div
            ref={tooltipRef}
            className={styles.tooltip}
            style={{ left: tooltip.left, top: tooltip.top }}
            role="status"
            aria-live="polite"
          >
            <div className={styles.tooltipTitle}>{tooltip.title}</div>
            <div className={styles.tooltipBody}>{tooltip.body}</div>
          </div>,
          document.body
        )}
    </div>
  )
}
